import { create } from 'zustand';
import storage, { generateLocalId } from '../services/storage';
import noteApi from '../services/noteApi';
import { syncService, SyncNoteItem } from '../services/syncService';
import { tokenStorage } from '../services/authService';
import type { Note, CreateNoteRequest, UpdateNoteRequest } from '../types/note';
import { EventType } from '../types/eventTypes';

interface NoteState {
    notes: Note[];
    isLoading: boolean;
    error: string | null;
    isEditorOpen: boolean;
    editingNote: Note | null;
    isSyncing: boolean;
    lastSyncTime: string | null;

    // Actions
    fetchNotes: (date: string) => Promise<void>;
    createNote: (data: CreateNoteRequest) => Promise<void>;
    updateNote: (id: string, data: UpdateNoteRequest) => Promise<void>;
    deleteNote: (id: string) => Promise<void>;
    openEditor: (note?: Note) => void;
    closeEditor: () => void;
    syncWithBackend: (date: string) => Promise<void>;
    cloudSync: () => Promise<void>;
}

export const useNoteStore = create<NoteState>((set, get) => ({
    notes: [],
    isLoading: false,
    error: null,
    isEditorOpen: false,
    editingNote: null,
    isSyncing: false,
    lastSyncTime: null,

    fetchNotes: async (date: string) => {
        set({ isLoading: true, error: null });

        try {
            // First load from local storage (instant)
            const localNotes = await storage.getNotesByDate(date);
            set({ notes: localNotes });

            // Then sync with backend
            try {
                const backendNotes = await noteApi.getNotesByDate(date);
                // Merge backend notes with local data to preserve fields like eventType
                const mergedNotes: Note[] = [];
                for (const backendNote of backendNotes) {
                    // Check if we have local data for this note
                    const localNote = localNotes.find(n => n.id === backendNote.id || n.localId === backendNote.id);
                    const merged: Note = {
                        ...backendNote,
                        // Preserve local-only fields (eventType, eventData, recurring) if they exist
                        eventType: localNote?.eventType || backendNote.eventType || EventType.KHAC,
                        eventData: localNote?.eventData || backendNote.eventData || {},
                        recurring: localNote?.recurring || backendNote.recurring,
                        tags: localNote?.tags || backendNote.tags || [],
                        synced: true,
                    };
                    await storage.saveNote(merged);
                    mergedNotes.push(merged);
                }
                set({ notes: mergedNotes, isLoading: false });
            } catch {
                // Offline - use local notes
                set({ isLoading: false });
            }
        } catch (error) {
            set({ error: 'Failed to load notes', isLoading: false });
        }
    },

    createNote: async (data: CreateNoteRequest) => {
        const now = new Date().toISOString();
        const localId = generateLocalId();

        const newNote: Note = {
            id: localId,
            localId,
            date: data.date,
            title: data.title,
            content: data.content,
            tags: data.tags || [],
            reminder: data.reminder,
            eventType: data.eventType || EventType.KHAC,
            eventData: data.eventData || {},
            recurring: data.recurring,
            created_at: now,
            updated_at: now,
            synced: false,
        };

        // Save locally first
        await storage.saveNote(newNote);
        set((state) => ({ notes: [newNote, ...state.notes] }));

        // Sync to backend
        try {
            const backendNote = await noteApi.createNote(data);
            // Merge backend response with local data to preserve fields like eventType
            // that the backend doesn't store yet
            const mergedNote: Note = {
                ...newNote,           // Start with local note (has eventType, eventData, recurring)
                ...backendNote,       // Override with backend data (id, timestamps)
                eventType: newNote.eventType,   // Preserve local eventType
                eventData: newNote.eventData,   // Preserve local eventData
                recurring: newNote.recurring,   // Preserve recurring config
                synced: true,
            };
            // Update local with backend ID
            await storage.deleteNote(localId);
            await storage.saveNote(mergedNote);
            set((state) => ({
                notes: state.notes.map((n) =>
                    n.localId === localId ? mergedNote : n
                ),
            }));
        } catch {
            // Add to sync queue for later
            await storage.addToSyncQueue('create', localId);
        }
    },

    updateNote: async (id: string, data: UpdateNoteRequest) => {
        const existingNote = get().notes.find((n) => n.id === id || n.localId === id);
        if (!existingNote) return;

        const updatedNote: Note = {
            ...existingNote,
            ...data,
            title: data.title ?? existingNote.title,
            content: data.content ?? existingNote.content,
            tags: data.tags ?? existingNote.tags,
            eventType: data.eventType ?? existingNote.eventType,
            eventData: data.eventData ?? existingNote.eventData,
            recurring: data.recurring ?? existingNote.recurring,
            updated_at: new Date().toISOString(),
            synced: false,
        };

        // Update locally first
        await storage.saveNote(updatedNote);
        set((state) => ({
            notes: state.notes.map((n) =>
                (n.id === id || n.localId === id) ? updatedNote : n
            ),
        }));

        // Sync to backend
        try {
            if (existingNote.id && !existingNote.id.startsWith('local_')) {
                const backendNote = await noteApi.updateNote(existingNote.id, data);
                // Merge backend response with local data to preserve eventType/eventData
                const mergedNote: Note = {
                    ...updatedNote,
                    ...backendNote,
                    eventType: updatedNote.eventType,
                    eventData: updatedNote.eventData,
                    recurring: updatedNote.recurring,
                    synced: true,
                };
                await storage.saveNote(mergedNote);
                set((state) => ({
                    notes: state.notes.map((n) =>
                        n.id === id ? mergedNote : n
                    ),
                }));
            }
        } catch {
            await storage.addToSyncQueue('update', id);
        }
    },

    deleteNote: async (id: string) => {
        // Remove from local
        await storage.deleteNote(id);
        set((state) => ({
            notes: state.notes.filter((n) => n.id !== id && n.localId !== id),
        }));

        // Delete from backend
        try {
            if (!id.startsWith('local_')) {
                await noteApi.deleteNote(id);
            }
        } catch {
            await storage.addToSyncQueue('delete', id);
        }
    },

    openEditor: (note?: Note) => {
        set({ isEditorOpen: true, editingNote: note || null });
    },

    closeEditor: () => {
        set({ isEditorOpen: false, editingNote: null });
    },

    syncWithBackend: async (date: string) => {
        // Process sync queue
        const queue = await storage.getSyncQueue();
        for (const item of queue) {
            try {
                const note = await storage.getNoteById(item.noteId);
                if (!note) continue;

                if (item.action === 'create') {
                    const backendNote = await noteApi.createNote({
                        date: note.date,
                        title: note.title,
                        content: note.content,
                        tags: note.tags,
                        reminder: note.reminder,
                        eventType: note.eventType,
                        eventData: note.eventData,
                    });
                    // Merge backend response with local data to preserve eventType/recurring
                    const mergedNote: Note = {
                        ...note,           // Start with local note (has eventType, eventData, recurring)
                        ...backendNote,    // Override with backend data (id, timestamps)
                        eventType: note.eventType,   // Preserve local eventType
                        eventData: note.eventData,   // Preserve local eventData
                        recurring: note.recurring,   // Preserve recurring config
                        synced: true,
                    };
                    await storage.deleteNote(item.noteId);
                    await storage.saveNote(mergedNote);
                } else if (item.action === 'update' && note.id) {
                    await noteApi.updateNote(note.id, {
                        title: note.title,
                        content: note.content,
                        tags: note.tags,
                    });
                } else if (item.action === 'delete' && note.id) {
                    await noteApi.deleteNote(note.id);
                }
            } catch {
                // Keep in queue for next sync
                continue;
            }
        }
        await storage.clearSyncQueue();

        // Refresh notes
        await get().fetchNotes(date);
    },

    // Cloud sync for authenticated users
    cloudSync: async () => {
        // Only sync if authenticated
        if (!tokenStorage.getToken()) {
            return;
        }

        set({ isSyncing: true });

        try {
            // Get all local notes
            const allLocalNotes = await storage.getAllNotes();

            // Convert to sync format
            const syncNotes: SyncNoteItem[] = allLocalNotes.map(note => ({
                id: note.id,
                localId: note.localId,
                date: note.date,
                title: note.title,
                content: note.content,
                eventType: note.eventType,
                isRecurring: !!note.recurring,
                tags: note.tags,
                reminder: note.reminder,
                updatedAt: note.updated_at,
                deleted: false
            }));

            // Sync with server
            const response = await syncService.syncNotes(syncNotes);

            // Process server notes (new or updated on server)
            for (const serverNote of response.serverNotes) {
                const localNote: Note = {
                    id: serverNote.id,
                    localId: serverNote.localId,
                    date: serverNote.date,
                    title: serverNote.title,
                    content: serverNote.content || '',
                    eventType: (serverNote.eventType as EventType) || EventType.KHAC,
                    eventData: {},
                    tags: serverNote.tags || [],
                    reminder: serverNote.reminder,
                    created_at: serverNote.updatedAt,
                    updated_at: serverNote.updatedAt,
                    synced: true
                };
                await storage.saveNote(localNote);
            }

            set({
                isSyncing: false,
                lastSyncTime: response.syncedAt
            });

            console.log(`Cloud sync complete: ${response.created} created, ${response.updated} updated, ${response.deleted} deleted`);
        } catch (error) {
            console.error('Cloud sync failed:', error);
            set({ isSyncing: false });
        }
    },
}));

export default useNoteStore;
